import type { ReactNode } from 'react';
import { Container } from './Container';

export interface SectionProps {
  children: ReactNode;
  id?: string;
  tone?: 'white' | 'muted' | 'navy' | 'primary';
  size?: 'default' | 'narrow';
  className?: string;
  'aria-labelledby'?: string;
}

const TONES = {
  white: 'bg-white',
  muted: 'bg-light-500',
  navy: 'bg-primary-700 text-white',
  primary: 'bg-primary-700 text-white',
};

export function Section({
  children,
  id,
  tone = 'white',
  size = 'default',
  className = '',
  ...rest
}: SectionProps) {
  return (
    <section id={id} className={`py-16 sm:py-20 lg:py-24 ${TONES[tone]} ${className}`} {...rest}>
      <Container size={size}>{children}</Container>
    </section>
  );
}

export interface SectionHeadingProps {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  id?: string;
  align?: 'left' | 'center';
  tone?: 'light' | 'dark';
}

export function SectionHeading({
  title,
  subtitle,
  eyebrow,
  id,
  align = 'center',
  tone = 'light',
}: SectionHeadingProps) {
  const alignment = align === 'center' ? 'text-center mx-auto' : 'text-left';

  return (
    <div className={`max-w-2xl ${alignment}`}>
      {eyebrow && (
        <p
          className={`flex items-center gap-3 text-[0.6875rem] font-semibold uppercase tracking-[0.18em] ${
            tone === 'dark' ? 'text-accent-300' : 'text-secondary-600'
          } ${align === 'center' ? 'justify-center' : ''}`}
        >
          <span aria-hidden="true" className="h-px w-8 bg-current opacity-50" />
          {eyebrow}
        </p>
      )}
      <h2
        id={id}
        className={`mt-5 font-display text-[2rem] font-black uppercase leading-[1.08] tracking-[-0.025em] sm:text-[2.5rem] lg:text-[2.875rem] ${
          tone === 'dark' ? 'text-white' : 'text-primary-500'
        }`}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className={`mt-5 text-lg font-light leading-[1.6] tracking-normal ${
            tone === 'dark' ? 'text-white/75' : 'text-slate-600'
          }`}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}
